import React, { useEffect, useState, useCallback } from "react";
import api, { apiError } from "@/lib/api";
import { PageHeader } from "@/components/common/PageHeader";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { Target, Save, Copy, SlidersHorizontal, Lock } from "lucide-react";

const MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
const nowYear = new Date().getFullYear();
const YEARS = [nowYear - 1, nowYear, nowYear + 1];
const sum = (a) => a.reduce((x, y) => x + (parseInt(y) || 0), 0);
const distribute = (annual, n = 12) => { annual = Math.max(0, parseInt(annual) || 0); const b = Math.floor(annual / n), r = annual % n; return Array.from({ length: n }, (_, i) => b + (i < r ? 1 : 0)); };
const fill = (rate, n = 12) => Array.from({ length: n }, () => Math.max(0, parseInt(rate) || 0));

export default function RecruitingGoals() {
  const [scopes, setScopes] = useState({ districts: [], can_edit_account_wide: false });
  const [catalog, setCatalog] = useState({ metrics: [], primary_options: [] });
  const [year, setYear] = useState(nowYear);
  const [districtId, setDistrictId] = useState("");   // "" = account-wide
  const [scope, setScope] = useState("group");
  const [goalUser, setGoalUser] = useState("");
  const [primary, setPrimary] = useState("career");
  const [plan, setPlan] = useState({});               // key -> {monthly:[12], authority}
  const [custom, setCustom] = useState(false);
  const [meta, setMeta] = useState(null);
  const [canEdit, setCanEdit] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.get("/recruiting/goals/scopes").then((r) => {
      setScopes(r.data);
      if (!r.data.can_edit_account_wide) {
        const hdr = localStorage.getItem("tr_district");
        const first = (r.data.districts || []).find((d) => d.id === hdr) || (r.data.districts || []).find((d) => d.can_edit) || (r.data.districts || [])[0];
        if (first) setDistrictId(first.id);
      }
    }).catch((e) => toast.error(apiError(e)));
    api.get("/recruiting/goals/metrics").then((r) => setCatalog(r.data)).catch(() => {});
  }, []);

  const load = useCallback(() => {
    if (scope === "personal" && !goalUser) { setPlan({}); setMeta(null); setCanEdit(false); return; }
    api.get("/recruiting/goals", { params: { year, district_org_id: districtId, goal_scope: scope, user_id: scope === "personal" ? goalUser : "" } }).then((r) => {
      setCanEdit(r.data.can_edit);
      const g = r.data.goal;
      if (g) {
        setPrimary(g.primary_metric);
        setPlan(g.metrics || {});
        setMeta({ updated_by_name: g.updated_by_name, updated_at: g.updated_at });
      } else {
        setPrimary("career");
        setPlan({ career: { monthly: distribute(12), authority: "annual" } });
        setMeta(null);
      }
      setCustom(false);
    }).catch((e) => toast.error(apiError(e)));
  }, [year, districtId, scope, goalUser]);
  useEffect(() => { load(); }, [load]);

  const measurable = catalog.metrics.filter((m) => m.measurable);
  const unavailable = catalog.metrics.filter((m) => !m.measurable);
  const enabled = (k) => k in plan;
  const annualOf = (k) => sum(plan[k]?.monthly || []);

  const setPrimaryMetric = (k) => {
    setPrimary(k);
    setPlan((p) => { const n = { ...p }; if (!n[k]) n[k] = { monthly: distribute(12), authority: "annual" }; else n[k] = { ...n[k], authority: "annual" }; return n; });
  };
  const setAnnual = (k, val) => setPlan((p) => ({ ...p, [k]: { ...(p[k] || {}), monthly: distribute(val), authority: "annual" } }));
  const setRate = (k, val) => setPlan((p) => ({ ...p, [k]: { ...(p[k] || {}), monthly: fill(val), authority: "monthly" } }));
  const setMonth = (k, i, val) => setPlan((p) => { const m = [...(p[k]?.monthly || Array(12).fill(0))]; m[i] = Math.max(0, parseInt(val) || 0); return { ...p, [k]: { ...(p[k] || {}), monthly: m } }; });
  const toggle = (k) => setPlan((p) => { const n = { ...p }; if (n[k]) { if (k === primary) return n; delete n[k]; } else n[k] = { monthly: fill(1), authority: "monthly" }; return n; });

  const copyPrev = async () => {
    try { await api.post("/recruiting/goals/copy", { from_year: year - 1, to_year: year, district_org_id: districtId, goal_scope: scope, user_id: goalUser }); toast.success(`Copied ${year - 1} goals to ${year}`); load(); }
    catch (e) { toast.error(apiError(e)); }
  };
  const save = async () => {
    if (!(primary in plan)) { toast.error("Add an annual target for the primary outcome"); return; }
    setSaving(true);
    try {
      const metrics = {}; Object.entries(plan).forEach(([k, v]) => { metrics[k] = { monthly: v.monthly, authority: v.authority || "monthly" }; });
      await api.put("/recruiting/goals", { year, district_org_id: districtId, goal_scope: scope, user_id: scope === "personal" ? goalUser : null, primary_metric: primary, metrics });
      toast.success("Recruiting goals saved"); load();
    } catch (e) { toast.error(apiError(e)); } finally { setSaving(false); }
  };

  const disabled = !canEdit;
  return (
    <div className="max-w-4xl">
      <PageHeader title="Recruiting Goals" description="Set management targets by year and district. Targets are compared against real recruiting results on the dashboard." />
      <div className="flex flex-wrap items-end gap-3 mb-5">
        <div><label className="text-xs font-medium text-slate-500 block mb-1">Year</label>
          <Select value={String(year)} onValueChange={(v) => setYear(Number(v))}>
            <SelectTrigger className="h-9 w-32" data-testid="goal-year"><SelectValue /></SelectTrigger>
            <SelectContent className="bg-white">{YEARS.map((y) => <SelectItem key={y} value={String(y)}>{y}</SelectItem>)}</SelectContent>
          </Select></div>
        <div><label className="text-xs font-medium text-slate-500 block mb-1">District / Group</label>
          <Select value={districtId || "__acct"} onValueChange={(v) => setDistrictId(v === "__acct" ? "" : v)}>
            <SelectTrigger className="h-9 w-64" data-testid="goal-district"><SelectValue /></SelectTrigger>
            <SelectContent className="bg-white">
              {scopes.can_edit_account_wide && <SelectItem value="__acct">Account-wide</SelectItem>}
              {scopes.districts.map((d) => <SelectItem key={d.id} value={d.id}>{d.name}</SelectItem>)}
            </SelectContent>
          </Select></div>
        <div><label className="text-xs font-medium text-slate-500 block mb-1">Goal For</label>
          <div className="flex h-9 rounded-md border border-slate-200 bg-white p-0.5" data-testid="goal-scope">
            {[["group", "Group / District"], ["personal", "Individual"]].map(([k, l]) => (
              <button key={k} onClick={() => setScope(k)} data-testid={`goal-scope-${k}`}
                className={`px-3 text-sm rounded ${scope === k ? "bg-brand text-white" : "text-slate-600 hover:bg-slate-50"}`}>{l}</button>))}
          </div></div>
        {scope === "personal" && <div><label className="text-xs font-medium text-slate-500 block mb-1">User</label>
          <Select value={goalUser || "__none"} onValueChange={(v) => setGoalUser(v === "__none" ? "" : v)}>
            <SelectTrigger className="h-9 w-56" data-testid="goal-user"><SelectValue placeholder="Choose a person" /></SelectTrigger>
            <SelectContent className="bg-white"><SelectItem value="__none">Choose a person…</SelectItem>
              {(scopes.districts.find((d) => d.id === districtId)?.members || []).map((u) => <SelectItem key={u.id} value={u.id}>{u.name}</SelectItem>)}</SelectContent>
          </Select></div>}
        <div className="flex-1" />
        {canEdit && <Button variant="outline" size="sm" onClick={copyPrev} data-testid="goal-copy-prev"><Copy className="w-4 h-4 mr-1.5" />Copy {year - 1} Goals</Button>}
      </div>

      {disabled && !(scope === "personal" && !goalUser) && <div className="mb-4 flex items-center gap-2 rounded-lg bg-slate-50 border border-slate-200 px-3 py-2 text-sm text-slate-500" data-testid="goal-readonly"><Lock className="w-4 h-4" />You have view-only access to goals for this scope.</div>}
      {scope === "personal" && !goalUser && <div className="mb-4 rounded-lg bg-sky-50 border border-sky-200 px-3 py-2 text-sm text-sky-800" data-testid="goal-choose-person">Choose a person to view or set their personal goal.</div>}

      <div className="rounded-xl border border-slate-200 bg-white p-5 shadow-sm space-y-5" data-testid="goal-form">
        <div className="flex items-center gap-2 text-brand"><Target className="w-4 h-4" /><h3 className="font-semibold text-slate-800">{scope === "personal" ? "Personal" : "Group"} Recruiting Goals — {year}</h3>
          <span className="text-[10px] font-bold uppercase tracking-wide rounded bg-brand/10 text-brand px-2 py-0.5" data-testid="goal-scope-badge">{scope === "personal" ? "Personal goal · counts only this person's results" : "Group goal · counts every result in this district"}</span></div>

        <div className="grid sm:grid-cols-2 gap-4">
          <div><label className="text-xs font-medium text-slate-500 block mb-1">Primary Outcome</label>
            <Select value={primary} onValueChange={setPrimaryMetric} disabled={disabled}>
              <SelectTrigger className="h-9" data-testid="goal-primary-metric"><SelectValue /></SelectTrigger>
              <SelectContent className="bg-white">{catalog.primary_options.map((m) => <SelectItem key={m.key} value={m.key}>{m.label}</SelectItem>)}</SelectContent>
            </Select></div>
          <div><label className="text-xs font-medium text-slate-500 block mb-1">Annual Goal</label>
            <Input type="number" min="0" className="h-9" data-testid="goal-primary-annual" disabled={disabled}
              value={annualOf(primary)} onChange={(e) => setAnnual(primary, e.target.value)} />
            <p className="text-[11px] text-slate-400 mt-1">Distributed to whole-number monthly targets totalling {annualOf(primary)}.</p></div>
        </div>

        <div>
          <p className="text-xs font-semibold text-slate-500 uppercase tracking-wide mb-2">Supporting Goals (per month)</p>
          <div className="grid sm:grid-cols-2 gap-2.5">
            {measurable.filter((m) => m.key !== primary).map((m) => (
              <div key={m.key} className="flex items-center gap-2.5 rounded-lg border border-slate-200 px-3 py-2" data-testid={`goal-support-${m.key}`}>
                <input type="checkbox" checked={enabled(m.key)} disabled={disabled} onChange={() => toggle(m.key)} className="rounded border-slate-300" data-testid={`goal-toggle-${m.key}`} />
                <span className="text-sm text-slate-700 flex-1">{m.label}</span>
                {enabled(m.key) && <Input type="number" min="0" className="h-8 w-20 text-sm" disabled={disabled} data-testid={`goal-rate-${m.key}`}
                  value={plan[m.key].authority === "monthly" ? (plan[m.key].monthly[0] ?? 0) : ""} onChange={(e) => setRate(m.key, e.target.value)} placeholder="/mo" />}
                {enabled(m.key) && <span className="text-[11px] text-slate-400 w-14 text-right">= {annualOf(m.key)}/yr</span>}
              </div>
            ))}
          </div>
          {unavailable.length > 0 && (
            <div className="mt-3">
              <p className="text-[11px] font-semibold text-slate-400 uppercase tracking-wide mb-1.5">Not yet measurable</p>
              <div className="flex flex-wrap gap-1.5">
                {unavailable.map((m) => <span key={m.key} title={m.reason} className="text-[11px] rounded-full bg-slate-100 text-slate-400 px-2 py-0.5 cursor-help" data-testid={`goal-unavailable-${m.key}`}>{m.label} · unavailable</span>)}
              </div>
            </div>
          )}
        </div>

        <div>
          <button className="text-xs text-brand font-medium flex items-center gap-1.5 mb-2" onClick={() => setCustom((c) => !c)} data-testid="goal-customize-toggle"><SlidersHorizontal className="w-3.5 h-3.5" />{custom ? "Hide monthly targets" : "Customize Monthly Targets"}</button>
          {custom && (
            <div className="space-y-3" data-testid="goal-monthly-editor">
              {Object.keys(plan).map((k) => (
                <div key={k} className="rounded-lg border border-slate-100 bg-slate-50/50 p-3" data-testid={`goal-months-${k}`}>
                  <div className="flex items-center justify-between mb-2"><span className="text-sm font-medium text-slate-700">{catalog.metrics.find((m) => m.key === k)?.label || k}</span><span className="text-xs text-slate-400">Annual Total {annualOf(k)}</span></div>
                  <div className="grid grid-cols-6 md:grid-cols-12 gap-1.5">
                    {MONTHS.map((mo, i) => (
                      <div key={mo} className="text-center"><span className="text-[10px] text-slate-400 block">{mo}</span>
                        <Input type="number" min="0" className="h-8 px-1 text-center text-sm" disabled={disabled} data-testid={`goal-month-${k}-${i}`}
                          value={plan[k].monthly[i] ?? 0} onChange={(e) => setMonth(k, i, e.target.value)} /></div>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        <div className="flex items-center justify-between pt-1 border-t border-slate-100">
          <p className="text-[11px] text-slate-400" data-testid="goal-meta">{meta?.updated_by_name ? `Last updated by ${meta.updated_by_name}${meta.updated_at ? " · " + new Date(meta.updated_at).toLocaleDateString() : ""}` : "Not yet saved"}</p>
          {canEdit && <Button onClick={save} disabled={saving} data-testid="goal-save" className="bg-brand hover:bg-brand-strong text-white"><Save className="w-4 h-4 mr-1.5" />{saving ? "Saving…" : "Save Goals"}</Button>}
        </div>
      </div>
    </div>
  );
}
